import { useGroupsStore } from '@/stores/groups';
import { useMessageStore } from '@/stores/messages'
import { useUsersStore } from '@/stores/users'
import { useChatStore } from '@/stores/chat'
import { useTimeoutFn } from '@vueuse/core'
import { uuid, musicReceiveMessage } from '@/helpers/index'
import { getChatSilence } from '@/helpers/chatSilence'
import services from '@/services'

import { TypingMessage, SuccessSendMessage, SuccessReadMessage } from '@/types/webSocketMessage'
import { SendMessage, ItemMessageChat, TypeMessage, ExtraMessageChat } from '@/types/itemsMessages'

export default function useChatInternal() {
  const messagesStore = useMessageStore()
  const userStore = useUsersStore()
  const groupStore = useGroupsStore()
  const chatStore = useChatStore()

  const { start, isPending, stop } = useTimeoutFn(() => {
    stop()
  }, 2000)

  function mountExtraMessage(selectedId: number, extra: ExtraMessageChat | null) {
    const key = chatStore.getWithMessages
    const isReply = messagesStore.getReply(selectedId, key)
    return isReply ? { reply : isReply} : extra
  }

  function mountMessage(selectedId : number, payload: SendMessage) {
    const result : ItemMessageChat = {
      uuid: uuid(),
      type: 'sent',
      submitted_by: userStore.me.id,
      message: {
        type: payload.type,
        content: payload.content,
        legend: payload?.legend ?? null,
        reply: payload.reply ?? null,
        extra: mountExtraMessage(selectedId, payload?.extra ?? null)
      },
      read: false,
      created_at: (new Date()).toISOString()
    }
    return result
  }

  function read(userId: number, type: TypeMessage = 'received') {
    if (userId) {
      messagesStore.read(userId, type)
      userStore.upLastMessage(userId)
    }
  }

  function typing(message: TypingMessage) {
    if (message?.to_user) {
      if (userStore.getById(message.submitted_by)) {
        userStore.upTyping(message.submitted_by, message.status)
      }
    }
    if (message?.to_group) {
        if(message.submitted_by === userStore.me.id) return
        if (groupStore.getById(message.to_group)) {
            groupStore.upTyping (message.to_group, message.submitted_by, message.status)
        }
    }
  }

  function execMusic(){
    if (chatStore.firstInstance && !getChatSilence()) {
        if (!isPending.value) {
            musicReceiveMessage()
        }
        start()
    }
  }

  function receive(payload: ItemMessageChat) {

    if (payload.to_send === 'user') {
        const userMessage = userStore.getById(payload.submitted_by)
        if (userMessage) {
            messagesStore.addMessage(userMessage.id, payload)
            messagesStore.read(payload.submitted_by, 'received')
        }
    } else if (payload.to_send === 'group') {
        const groupMessage = groupStore.getById(payload?.group ?? 0)
        if (groupMessage) {
            messagesStore.addMessage(groupMessage.id, payload, 'push', 'groups')
            messagesStore.read(groupMessage.id, 'received','groups')
        }
    }
    execMusic()
  }

  function sendSuccess(payload: SuccessSendMessage) {
    if (payload?.to_user) {
      messagesStore.editUserMessage(payload)
      userStore.upLastMessage(payload.to_user)
    }
    if (payload?.to_group) {
      messagesStore.editGroupMessage(payload)
      groupStore.upLastMessage(payload.to_group)
    }
  }

  function readSuccess(payload: SuccessReadMessage) {
    if (payload?.to_user) {
      messagesStore.read(payload.to_user, 'received')
      userStore.upCountUnreadMessage(payload.to_user, true)
    }
    if (payload?.to_group) {
      messagesStore.confirmationUserMessage(payload.to_group)
      groupStore.upCountUnreadMessage(payload.to_group, true)
    }
  }

  function usersOnline(payload: number[]) {
    if (payload) {
      userStore.upStatus(payload)
    }
  }

  async function favoriteMessage(id: number) {
    if(!id) {
        console.error('id not informed')
        return
    }
    const message = messagesStore.getById(id)
    if (message) {
        const chatStore = useChatStore()
        if (chatStore.selected?.id) {
            const userId = chatStore.selected.id
            const type = chatStore.getWithService
            const isWith = chatStore.getWithMessages
            const { data, errors } = await services[type].saveFavorite(userId, id)
            if( errors ) {
                console.error(errors)
                return
            }
            messagesStore.addFavorite(data, userId, isWith)
            messagesStore.setMessageFavorite(userId, id, 1, isWith)
        } else {
            console.error('not chat selected')
        }
    }else{
        console.error(id, ' - message not localized - ', message)
        return false
    }

  }

  async function remFavoriteMessage(id: number) {
    if(!id) {
        console.error('id not informed')
        return
    }
    const message = messagesStore.getById(id)
    if (message?.id ) {
        const chatStore = useChatStore()
        if (chatStore.selected?.id) {
            const userId = chatStore.selected.id
            const type = chatStore.getWithService
            const isWith = chatStore.getWithMessages
            const { errors } = await services[type].removeFavorite(userId, id)
            if( errors ) {
                console.error(errors)
                return
            }
            messagesStore.removeFavorite(message.id, userId, isWith)
            messagesStore.setMessageFavorite(userId, id, null, isWith )
        } else {
            console.error('not chat selected')
        }
    }
    console.error('message not localized')
    return false
  }

  function addReply(id: number): boolean {
    if(!id) {
        console.error('id not informed')
        return false
    }
    const message = messagesStore.getById(id)
    if (message ) {
        if (chatStore.selected?.id) {
            const userId = chatStore.selected.id
            const key = chatStore.getWithMessages
            messagesStore.addReply(message, userId, key)
            return true
        } else {
            console.error('not chat selected')
            return false
        }
    }
    console.error('message not localized ' + id)
    return false
  }

  function firstInstance( status : boolean = false) {
    chatStore.upFirstInstance(status)
  }

  return {
    read, typing, receive, sendSuccess, readSuccess, mountMessage, usersOnline,
    favoriteMessage, remFavoriteMessage, addReply, firstInstance }
}
